/**
 * Transcript download helpers.
 *
 * The TranscriptDrawer already has the full transcript in memory (the S3
 * checkpoint JSON). These helpers serialize it to a human-readable Markdown
 * file or the raw JSON, and trigger a browser download. Serialization is pure +
 * dependency-free so it's unit-testable; only `downloadTranscript` touches the DOM.
 */
import type { TranscriptView, TranscriptContentBlock, TranscriptMessage } from '../../../shared/types/epics';

export type TranscriptFormat = 'md' | 'json';

/**
 * Filesystem-safe, COLLISION-FREE filename, e.g.
 *   transcript-epic40-backend-0-iter10-a1b2c3d4.json
 *
 * FORGE-248: the old name (`transcript-<agent>-<story>-iter<N>`) omitted the
 * epic number and the task id, so every epic's "backend story-0 iter-10"
 * checkpoint downloaded to the SAME filename — the browser then piled them up as
 * ` (1)`, ` (2)`, … making unrelated epics' transcripts look like one giant set
 * and impossible to tell apart. We now derive the epic number + task id from the
 * s3Key so each download is unique and self-identifying.
 */
export function transcriptFilename(t: TranscriptView, format: TranscriptFormat): string {
  const safe = (s: string) => String(s).replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  const s3Key = t.s3Key ?? '';
  const epic = /EPIC#(\d+)/.exec(s3Key)?.[1];
  const task = /task-([0-9a-fA-F]+)/.exec(s3Key)?.[1]?.slice(0, 8);
  const parts = [
    'transcript',
    epic ? `epic${epic}` : '',
    safe(t.agentType),
    safe(t.storyId),
    `iter${t.iteration}`,
    task ?? '',
  ].filter(Boolean);
  return `${parts.join('-')}.${format}`;
}

function blockToMarkdown(b: TranscriptContentBlock): string {
  switch (b.type) {
    case 'text':
      return b.text ?? '';
    case 'thinking':
      return `<details><summary>thinking</summary>\n\n\`\`\`\n${b.thinking ?? ''}\n\`\`\`\n\n</details>`;
    case 'tool_use':
      return `**🔧 tool_use · ${b.name}**\n\n\`\`\`json\n${safeStringify(b.input)}\n\`\`\``;
    case 'tool_result': {
      const label = b.is_error ? '**⚠️ tool_result (error)**' : '**tool_result**';
      const body = typeof b.content === 'string' ? b.content : safeStringify(b.content);
      return `${label}\n\n\`\`\`\n${body}\n\`\`\``;
    }
    case 'image':
      return `_[image · ${b.source?.media_type ?? 'unknown'}]_`;
    default:
      return '```\n' + safeStringify(b) + '\n```';
  }
}

function normaliseContent(content: TranscriptMessage['content']): TranscriptContentBlock[] {
  if (typeof content === 'string') return [{ type: 'text', text: content }];
  if (Array.isArray(content)) return content;
  return [];
}

/** Render the transcript as a readable Markdown document. */
export function transcriptToMarkdown(t: TranscriptView): string {
  const header = [
    `# Transcript — ${t.agentType} · ${t.storyId}`,
    '',
    `- iteration: ${t.iteration}`,
    `- reason: ${t.reason ?? 'n/a'}`,
    `- messages: ${t.messages.length}`,
    `- size: ${t.bytes} bytes`,
    `- source: \`${t.s3Key}\``,
    '',
    '---',
    '',
  ].join('\n');

  const body = t.messages
    .map((msg, i) => {
      const blocks = normaliseContent(msg.content).map(blockToMarkdown).join('\n\n');
      return `## [${i + 1}] ${msg.role}\n\n${blocks}`;
    })
    .join('\n\n---\n\n');

  return `${header}${body}\n`;
}

/** Pretty-printed raw JSON of the transcript. */
export function transcriptToJson(t: TranscriptView): string {
  return safeStringify(t);
}

/** Serialize + trigger a browser download of the transcript in the given format. */
export function downloadTranscript(t: TranscriptView, format: TranscriptFormat): void {
  const content = format === 'json' ? transcriptToJson(t) : transcriptToMarkdown(t);
  const mime = format === 'json' ? 'application/json' : 'text/markdown';
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = transcriptFilename(t, format);
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick so the download has started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
